import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import { 
  UserGroupIcon, 
  TrophyIcon, 
  ChartBarIcon, 
  GlobeAltIcon 
} from '@heroicons/react/24/outline';

const AboutSection = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const stats = [
    {
      icon: TrophyIcon,
      value: "8+",
      label: "Years Combined Experience",
      description: "Deep expertise in traditional finance and crypto markets"
    },
    {
      icon: ChartBarIcon,
      value: "$2.5B+",
      label: "Assets Under Analysis",
      description: "Comprehensive coverage across DeFi and CeFi protocols"
    },
    {
      icon: UserGroupIcon,
      value: "150+",
      label: "Successful Engagements",
      description: "Trusted by leading protocols and institutional clients"
    },
    {
      icon: GlobeAltIcon,
      value: "25+",
      label: "Countries Served",
      description: "Global reach with local market expertise"
    }
  ];

  const differentiators = [
    {
      title: "Elite Pedigree",
      description: "Founded by former McKinsey consultants and Wall Street traders with deep crypto-native experience."
    },
    {
      title: "Quantitative Approach",
      description: "Data-driven methodologies combining traditional finance principles with on-chain analytics."
    },
    {
      title: "End-to-End Solutions",
      description: "From strategy to implementation, we provide comprehensive support across the entire liquidity lifecycle."
    },
    {
      title: "Institutional Quality",
      description: "We deliver the same caliber of analysis and recommendations you'd expect from top-tier consulting firms."
    }
  ];

  return (
    <section className="py-24 bg-gradient-to-b from-primary-800 to-primary-900">
      <div className="section-padding">
        <div className="container-width">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left Side - Content */}
            <motion.div
              ref={ref}
              initial={{ opacity: 0, x: -30 }}
              animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -30 }}
              transition={{ duration: 0.8 }}
            >
              <div className="inline-block px-6 py-2 glass-card text-accent-gold text-sm font-medium tracking-wide uppercase mb-6">
                About Valeon
              </div>
              
              <h2 className="text-display font-serif font-bold text-cream-100 mb-6">
                Where Traditional Finance{' '}
                <span className="text-gradient">Meets Crypto Innovation</span>
              </h2>
              
              <div className="space-y-6 text-cream-200 text-body-lg leading-relaxed mb-8">
                <p>
                  Valeon is led by a seasoned team of financial market experts and crypto-native 
                  strategists with extensive experience at leading institutions including McKinsey & Company, 
                  Goldman Sachs, and top-tier crypto trading firms.
                </p>
                
                <p>
                  We bridge the gap between traditional finance expertise and cutting-edge blockchain 
                  technology, providing institutional-grade advisory services specifically tailored 
                  for the token economy.
                </p>
              </div>

              {/* Differentiators */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
                {differentiators.map((item, index) => (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 20 }}
                    animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="glass-card p-6"
                  >
                    <h4 className="font-semibold text-cream-100 mb-2">{item.title}</h4>
                    <p className="text-sm text-cream-300">{item.description}</p>
                  </motion.div>
                ))}
              </div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <Link to="/team" className="btn-primary">
                  Learn About Our Team
                </Link>
              </motion.div>
            </motion.div>

            {/* Right Side - Image and Stats */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: 30 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              {/* Background Image */}
              <div className="relative rounded-3xl overflow-hidden mb-8">
                <img 
                  src="https://images.unsplash.com/photo-1495576775051-8af0d10f19b1"
                  alt="Modern office space representing Valeon's professional environment"
                  className="w-full h-80 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-primary-900/60 to-transparent" />
                
                {/* Floating Card */}
                <div className="absolute -bottom-6 -left-6 glass-card p-6 max-w-xs">
                  <div className="text-heading-md font-bold text-cream-100 mb-1">
                    Elite Expertise
                  </div>
                  <div className="text-cream-200 text-sm">
                    Combining institutional finance experience with deep crypto market knowledge
                  </div>
                </div>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 gap-4">
                {stats.map((stat, index) => (
                  <motion.div
                    key={stat.label}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={inView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
                    className="glass-card p-6 text-center group hover:scale-105 transition-transform duration-300"
                  >
                    <div className="mb-4">
                      <stat.icon className="h-8 w-8 text-accent-blue mx-auto group-hover:text-accent-gold transition-colors duration-300" />
                    </div>
                    <div className="text-heading-md font-bold text-cream-100 mb-1">
                      {stat.value}
                    </div>
                    <div className="text-xs text-cream-200 font-medium mb-2">
                      {stat.label}
                    </div>
                    <div className="text-xs text-cream-300 leading-tight">
                      {stat.description}
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;